import { useEffect, useState } from "react";
import { clockTime } from "@buildlab/shared/golf";
import { golfApi, type GolfBestPrompt, type GolfBoard } from "./api";
import { Avatar } from "./Sprite";

// Prompt Golf's board for one hole (D53): each player's best round in the class, fewest strokes
// first, then fewest words, then fastest. Names are First L. with their icon. Teachers pick the
// class, can start a board over, and can reveal the leader's messages to talk about on the
// projector; students never see anyone's messages.

export function GolfBoardDialog({ holeId, title, par, teacher, onClose }: { holeId: string; title: string; par: number; teacher: boolean; onClose: () => void }) {
  const [classId, setClassId] = useState<string | undefined>(undefined);
  const [board, setBoard] = useState<GolfBoard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [best, setBest] = useState<GolfBestPrompt | null | "none">(null);
  const [busy, setBusy] = useState(false);

  const load = (id: string | undefined) =>
    golfApi.board(holeId, id).then(
      (b) => {
        setBoard(b);
        setError(null);
      },
      (e: Error) => setError(e.message),
    );
  useEffect(() => {
    setBest(null);
    void load(classId);
  }, [holeId, classId]);

  const reveal = async () => {
    setBusy(true);
    try {
      const r = await golfApi.bestPrompt(holeId, board?.classId ?? "");
      setBest(r.best ?? "none");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!board || !window.confirm(`Start the ${title} board over for ${board.className}? Everyone keeps their own best score.`)) return;
    setBusy(true);
    try {
      await golfApi.resetBoard(holeId, board.classId ?? "");
      setBest(null);
      await load(board.classId ?? "");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="golf-card-backdrop" role="dialog" aria-modal="true" aria-label={`Board: ${title}`} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="golf-board">
        <div className="row between golf-board-head">
          <div>
            <span className="eyebrow">Board · par {par}</span>
            <h2>{title}</h2>
          </div>
          <button type="button" className="icon-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        {teacher && board?.classes && (
          <label className="golf-board-class">
            <span className="small muted">Class</span>
            <select value={board.classId ?? ""} onChange={(e) => setClassId(e.target.value)}>
              {board.classes.map((k) => (
                <option key={k.id || "teachers"} value={k.id}>
                  {k.name}
                  {k.demo ? " (demo)" : ""}
                </option>
              ))}
            </select>
          </label>
        )}
        {!teacher && board && <p className="small muted">{board.className}: fewest strokes wins, then fewest words, then fastest.</p>}
        {error && <p className="page-error small">{error}</p>}
        {board && board.board.length === 0 && <p className="golf-board-empty">No one has rebuilt this one yet. Be the first!</p>}
        {board && board.board.length > 0 && (
          <ol className="golf-board-list">
            <li className="golf-board-row head" aria-hidden="true">
              <span className="r">#</span>
              <span className="n">Player</span>
              <span className="s">Strokes</span>
              <span className="w">Words</span>
              <span className="t">Time</span>
            </li>
            {board.board.map((r, i) => (
              <li key={i} className={`golf-board-row${r.me ? " me" : ""}${r.rank === 1 ? " first" : ""}`}>
                <span className="r">{r.rank}</span>
                <span className="n">
                  <Avatar name={r.name} avatar={r.avatar} size={16} />
                  <span className="nm">
                    {r.name}
                    {r.me ? " (you)" : ""}
                  </span>
                  <span className={`golf-tag k-${r.score.kind}`}>{r.score.label}</span>
                </span>
                <span className="s">{r.strokes}</span>
                <span className="w">{r.words}</span>
                <span className="t">{clockTime(r.seconds)}</span>
              </li>
            ))}
          </ol>
        )}
        {teacher && board && (
          <div className="golf-teacher">
            <div className="row">
              <button type="button" className="outline small" disabled={busy || board.board.length === 0} onClick={() => void reveal()}>
                Show the best prompt
              </button>
              <button type="button" className="linklike small" disabled={busy} onClick={() => void reset()}>
                Reset this board
              </button>
              <span className="small muted">Only teachers see these.</span>
            </div>
            {best === "none" && <p className="small muted">No finished rounds on this board yet.</p>}
            {best && best !== "none" && (
              <div className="golf-best">
                <p className="small">
                  <b>{best.name}</b> · {best.score.label} · {best.strokes} {best.strokes === 1 ? "stroke" : "strokes"}, {best.words} words
                </p>
                <ol>
                  {best.messages.map((m, i) => (
                    <li key={i}>
                      <blockquote>{m}</blockquote>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
